import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StepPickup from './delivery-task-page/StepPickup';
import StepDelivery from './delivery-task-page/StepDelivery';
import StepPackage from './delivery-task-page/StepPackage';
import Payment from './payment/Payment';
import type { PickupData } from './delivery-task-page/StepPickup';
import type { DeliveryData } from './delivery-task-page/StepDelivery';
import type { PackageData } from './delivery-task-page/StepPackage';
import 'pages/home/components/homepage/homepage.css';

type Step = 1 | 2 | 3 | 4;

const STEPS: { label: string; icon: string }[] = [
  { label: 'Pick-Up',  icon: 'location_on'    },
  { label: 'Delivery', icon: 'local_shipping'  },
  { label: 'Package',  icon: 'package_2'       },
  { label: 'Payment',  icon: 'credit_card'     },
];

const HERO: Record<Step, { badge: string; title: () => JSX.Element; subtitle: string }> = {
  1: {
    badge: 'New Delivery',
    title: () => <><span className="text-primary italic skew-heading">Pick-up</span> details</>,
    subtitle: "Who's sending it and where from?",
  },
  2: {
    badge: 'New Delivery',
    title: () => <>Delivery <span className="text-primary italic skew-heading">details</span></>,
    subtitle: 'Where is it going and who receives it?',
  },
  3: {
    badge: 'New Delivery',
    title: () => <>What&apos;s in the <span className="text-primary italic skew-heading">package</span>?</>,
    subtitle: 'Help your runner handle it with care.',
  },
  4: {
    badge: 'Confirm & Pay',
    title: () => <>Review your <span className="text-primary italic skew-heading">order</span></>,
    subtitle: "Double-check the details and choose how you'd like to pay.",
  },
};

export default function DeliveryFlowPage() {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<Step>(1);

  const [pickupData, setPickupData]     = useState<PickupData | null>(null);
  const [deliveryData, setDeliveryData] = useState<DeliveryData | null>(null);
  const [packageData, setPackageData]   = useState<PackageData | null>(null);

  /* ── Scroll-reveal ── */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // Reset visibility for newly mounted fade-up elements
    root.querySelectorAll('.fade-up').forEach((el) => el.classList.remove('visible'));
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const delay = parseFloat((entry.target as HTMLElement).dataset.delay ?? '0');
          setTimeout(() => entry.target.classList.add('visible'), delay * 1000);
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.08 },
    );
    root.querySelectorAll('.fade-up').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [step]);

  const advance = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStep((s) => Math.min(s + 1, 4) as Step);
  };

  const retreat = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStep((s) => Math.max(s - 1, 1) as Step);
  };

  const hero = HERO[step];

  // Full order data assembled for Payment step
  const orderFormData =
    pickupData && deliveryData && packageData
      ? { ...pickupData, ...deliveryData, ...packageData }
      : null;

  return (
    <div
      ref={rootRef}
      className="min-h-screen bg-background-light font-display text-slate-900 overflow-x-hidden"
    >
      {/* ── Nav ── */}
      <header className="sticky top-0 z-50 bg-background-light/85 backdrop-blur-xl border-b border-primary/8 px-6 lg:px-20 py-4 nav-enter">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="bg-primary p-1.5 rounded-xl shadow-lg shadow-primary/25">
              <span
                className="material-symbols-outlined text-white text-[22px]"
                style={{ fontVariationSettings: '"FILL" 1' }}
              >
                directions_run
              </span>
            </div>
            <h2 className="text-[1.4rem] font-black tracking-tighter">Errandi</h2>
          </Link>

          <button
            onClick={() => (step === 1 ? navigate(-1) : retreat())}
            className="flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-primary transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            <span className="hidden sm:block">Back</span>
          </button>
        </div>
      </header>

      {/* ── Hero strip ── */}
      <section className="relative overflow-hidden bg-background-light pt-10 pb-8 px-6">
        <div className="absolute inset-0 hero-pattern opacity-[0.04] pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-primary/10 to-orange-100/30 blur-3xl pointer-events-none orb-drift" />

        <div className="max-w-3xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-xs font-black tracking-widest uppercase mb-4 hero-line-1">
            <span className="w-2 h-2 bg-primary rounded-full status-blink" />
            {hero.badge}
          </div>
          <h1 className="hero-line-2 text-3xl md:text-4xl font-black leading-tight tracking-tight mb-2">
            {hero.title()}
          </h1>
          <p className="hero-line-3 text-slate-500 text-sm font-medium max-w-md">
            {hero.subtitle}
          </p>
        </div>
      </section>

      {/* ── Step indicator ── */}
      <div className="px-6 pb-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center">
            {STEPS.map((s, i) => {
              const num = (i + 1) as Step;
              const done = step > num;
              const active = step === num;
              const isLast = i === STEPS.length - 1;
              return (
                <div key={s.label} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all
                        ${done || active ? 'bg-primary text-white shadow-md shadow-primary/30' : 'bg-slate-200 text-slate-400'}`}
                    >
                      {done ? (
                        <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: '"FILL" 1' }}>check</span>
                      ) : (
                        num
                      )}
                    </div>
                    <span className={`text-[10px] font-black tracking-widest uppercase transition-colors hidden sm:block
                      ${active ? 'text-slate-800' : done ? 'text-primary' : 'text-slate-400'}`}>
                      {s.label}
                    </span>
                  </div>
                  {!isLast && (
                    <div className="flex-1 h-px bg-slate-200 mx-2 relative overflow-hidden">
                      <div className={`absolute inset-y-0 left-0 bg-primary transition-all duration-500 ${done ? 'w-full' : 'w-0'}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Step content ── */}
      <div className="px-6 pb-24">
        <div className="max-w-3xl mx-auto">
          {step === 1 && (
            <StepPickup
              defaultValues={pickupData ?? undefined}
              onNext={(data) => { setPickupData(data); advance(); }}
            />
          )}
          {step === 2 && (
            <StepDelivery
              defaultValues={deliveryData ?? undefined}
              onNext={(data) => { setDeliveryData(data); advance(); }}
              onBack={retreat}
            />
          )}
          {step === 3 && (
            <StepPackage
              defaultValues={packageData ?? undefined}
              onNext={(data) => { setPackageData(data); advance(); }}
              onBack={retreat}
            />
          )}
          {step === 4 && orderFormData && (
            <Payment orderFormData={orderFormData} onBack={retreat} />
          )}
        </div>
      </div>
    </div>
  );
}

